import { useState, useRef, useEffect } from 'react';
import { getExhibitionThemeClassName, getExhibitionThemeStyle } from '@/features/exhibitions/utils/exhibitionColors';
import trashIcon from '@/assets/svg/trash_icon.svg';

interface AudioPlayerProps {
    audioUrl: string;
    onRemove: () => void;
    /** Couleur de l'exposition pour le thème du lecteur (aligné sur la PWA) */
    exhibitionColor?: string;
}

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ audioUrl, onRemove, exhibitionColor }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const audioPlayerRef = useRef<HTMLAudioElement>(null);

    useEffect(() => {
        const audio = audioPlayerRef.current;
        if (!audio) return;

        const updateTime = () => setCurrentTime(audio.currentTime);
        const updateDuration = () => setDuration(audio.duration);
        const handleEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('timeupdate', updateTime);
        audio.addEventListener('loadedmetadata', updateDuration);
        audio.addEventListener('ended', handleEnded);

        return () => {
            audio.removeEventListener('timeupdate', updateTime);
            audio.removeEventListener('loadedmetadata', updateDuration);
            audio.removeEventListener('ended', handleEnded);
        };
    }, [audioUrl]);

    useEffect(() => {
        const audio = audioPlayerRef.current;
        if (!audio) return;

        if (isPlaying) {
            audio.play();
        } else {
            audio.pause();
        }
    }, [isPlaying]);

    const handlePlayPause = () => {
        setIsPlaying(!isPlaying);
    };

    const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
        const audio = audioPlayerRef.current;
        if (!audio || !duration) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percentage = clickX / rect.width;
        const newTime = percentage * duration;

        audio.currentTime = newTime;
        setCurrentTime(newTime);
    };

    const formatTime = (seconds: number): string => {
        if (isNaN(seconds) || !isFinite(seconds)) return '00:00';
        const mins = Math.floor(Math.abs(seconds) / 60);
        const secs = Math.floor(Math.abs(seconds) % 60);
        const sign = seconds < 0 ? '-' : '';
        return `${sign}${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div
            className={`audio-player ${getExhibitionThemeClassName(exhibitionColor, 'audio-player')}`}
            style={getExhibitionThemeStyle(exhibitionColor)}
        >
            <audio ref={audioPlayerRef} src={audioUrl} />
            <button className="play-button" onClick={handlePlayPause}>
                {isPlaying ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="2" width="2" height="12" fill="white" />
                        <rect x="9" y="2" width="2" height="12" fill="white" />
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 2L14 8L4 14V2Z" fill="white" />
                    </svg>
                )}
            </button>
            <div className="audio-progress-container" onClick={handleProgressClick}>
                <div className="audio-progress-bar">
                    <div
                        className="audio-progress-fill"
                        style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                    />
                </div>
            </div>
            <div className="audio-time">{formatTime(currentTime - duration)}</div>
            <button className="audio-delete-button" onClick={onRemove}>
                <img src={trashIcon} alt="delete" />
            </button>
        </div>
    );
};

